import { Apollo, gql } from 'apollo-angular';
import { Injectable } from '@angular/core';
import { BaseRepository } from 'src/app/services/remote-data-service/repositories/base.repository';
import { TimeTrackMapper } from 'src/app/services/remote-data-service/mappers/time-track.mapper';
import { TimeTrack } from 'src/app/entities/time-track/time-track';
import { TimeTrackDto } from 'src/app/services/remote-data-service/dtos/time-track.dto';
import { DateService } from 'src/app/services/date-service/date.service';
import { catchError, map } from 'rxjs';
import log from 'loglevel';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';


/**
 * GraphQL arguments for `get_time_tracks` query.
 */
export interface TimeTracksFilters {
  page?: number;
  limit?: number;
  from?: string;
  to?: string;
  authors?: string[];
  tags?: string[];
  projects?: string[];
  title?: string;
}


/**
 * GraphQL query used by this gateway.
 *
 * Exported because is required by tests.
 */
@Injectable({
  providedIn: 'root',
})
export class TimeTrackRepository extends BaseRepository<
  TimeTrack,
  TimeTrackDto
> {
  protected override _mapper = new TimeTrackMapper();
  protected override dataPropertyName = 'get_time_tracks';

  protected override logger = log.getLogger('TimeTrackRepository');

  constructor(
    protected override apollo: Apollo,
    protected override snackBar: SnackbarService,
    protected dateService: DateService
  ) {
    super(apollo, snackBar);
  }

  /******************/
  /* Public methods */
  /******************/
  public override getGqlQuery() {
    return gql`
      query TimeTracksQuery($page: Int, $limit: Int, $from: String, $to: String, $authors: [ID], $tags: [ID], $projects: [ID], $title: String) {
        get_time_tracks (page: $page, limit: $limit, from: $from, to: $to, authors: $authors, tags: $tags, projects: $projects, title: $title) {
          pager {
            current
            next
            prev
          }
          total
          items {
            title
            uuid
            from
            to
            time_spent
            author {
              name
              uuid
            }
            project {
              uuid
              title
              color
              archived
              active
              permissions {
                update
                delete
                track
              }
              client {
                uuid
                name
              }
            }
            tags {
              uuid
              name
            }
            permissions {
              update
              delete
            }
          }
        }
      }
    `;
  }


  /**
   * Function to add new track
   * @param track
   * @returns A boolean observable that indicates if query was successful.
   */
  addNewTrack(track: TimeTrack) {
    const ADDTRACK = gql`
      mutation MyMutation($id: String, $title: String, $from: String!, $to: String, $author: String, $project: String, $tags: [String]) {
        create_time_track(
          data: {uuid: $id, title: $title, from: $from, to: $to, billable: true, author: $author, project: $project, tags: $tags }
        ) {
          time_track {
            uuid
            title
            from
            to
            project {
                title
                uuid
            }
            author {
                name
                mail
                uuid
            }
            tags {
                name
            }
            time_spent
            permissions {
              update
              delete
            }
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: ADDTRACK,
        variables: {
          id: track.id,
          title: track.title,
          from: this.dateService.getLocalIsosString(track.from),
          to: track.to ? this.dateService.getLocalIsosString(track.to) : null,
          author: track.author?.id,
          project: track.project?.id,
          tags: track.tags.map(tag => tag.id)
        },
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error)
          return [];
        })
      );
  }


  removeTrack(track: TimeTrack) {
    const REMOVETRACK = gql`
      mutation MyMutation {
        delete_time_track(uuid: "${track.id}") {
          time_track {
            title
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: REMOVETRACK,
        variables: {},
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        })
      );
  }


  updateTrack(track: TimeTrack) {
    const UPDATETRACK = gql`
      mutation MyMutation($id: ID!, $title: String, $author: String, $project: String, $tags: [String], $from: String, $to: String) {
        update_time_track(
          uuid: $id,
          data: {title: $title, from: $from, to: $to, author: $author, billable: true, project: $project, tags: $tags }
        ) {
          time_track {
            uuid
            title
            to
            from
            tags {
              uuid
            }
            project {
                title
                uuid
            }
            author {
                name
                mail
                uuid
            }
            permissions {
              update
              delete
            }
          }
          errors
        }
      }
    `;

    return this.apollo
      .mutate({
        mutation: UPDATETRACK,
        variables: {
          id: track.id,
          title: track.title,
          author: track.author?.id || null,
          project: track.project ? track.project.id : null,
          tags: track.tags.map(tag => tag.id),
          from: this.dateService.getLocalIsosString(track.from),
          to: track.to ? this.dateService.getLocalIsosString(track.to) : null,
        },
      })
      .pipe(
        map(() => true),
        catchError((error) => {
          this.logger.error(error);
          this.snackBar.showError(error);
          return [];
        })
      );
  }
}
